import '@atlaskit/css-reset';
import React, { useEffect, useRef } from 'react';
import { useQueryLoader } from 'react-relay';
import { setMark } from '@atlassian/jira-common-performance/src/marks.tsx';
import { componentWithFG } from '@atlassian/jira-feature-gate-component';
import { useFeatures } from '@atlassian/jira-navigation-apps-sidebar-software-classic/src/controllers/features/index.tsx';
import { createAri } from '@atlassian/jira-platform-ari';
import RedirectIfProjectArchived from '@atlassian/jira-redirect-archived-project/src/ui/index.tsx';
import { useRelayResource } from '@atlassian/jira-relay-utils/src/services/resources/index.tsx';
import type { viewBacklogCustomFiltersQuery } from '@atlassian/jira-relay/src/__generated__/viewBacklogCustomFiltersQuery.graphql';
import VERSION_FILTER_RELAY_QUERY, {
	type viewBacklogVersionFilterRelayQuery,
} from '@atlassian/jira-relay/src/__generated__/viewBacklogVersionFilterRelayQuery.graphql';
import { customFiltersResource } from '@atlassian/jira-router-resources-next-gen-backlog/src/services/custom-filters/index.tsx';
import {
	uifBacklogResource,
	uifBacklogResourceOld,
} from '@atlassian/jira-router-resources-uif-backlog';
import type { JiraPlanDataRestResponse } from '@atlassian/jira-software-backlog-uif-types/src/index.tsx';
import ResourceRefreshOnMount from '@atlassian/jira-software-resource-invalidator/src/ui/resource-refresh-on-mount/index.tsx';
import type { UIFBacklogCachedDataResult } from '@atlassian/jira-software-uif-early-script';
import { SubProductUpdater } from '@atlassian/jira-spa-apps-common/src/analytics-sub-product/sub-product-updater/index.tsx';
import { Breadcrumbs } from '@atlassian/jira-spa-apps-software-breadcrumbs/src/ui/index.tsx';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import { Redirect, usePathParam, useResource } from '@atlassian/react-resource-router';
import {
	BACKLOG_PREFETCHED_DATA_READY_MARK,
	USE_RESOURCE_READY_MARK,
	USE_RESOURCE_START_MARK,
} from '../../../../common/constants';
import { getCMPBoardUrl, useOnce } from '../../../../common/utils';
import BacklogApp from './backlog-app';

export const BacklogMetaPlanDataAPIConsumerOld = () => {
	const [paramProjectKey] = usePathParam('projectKey');
	const [boardIdStr] = usePathParam('boardId');
	const boardId = Number(boardIdStr);
	const projectKey = paramProjectKey ?? '';

	const tenantContext = useTenantContext();

	useOnce(() => {
		setMark(USE_RESOURCE_START_MARK);
	});

	const jiraPlanDataRestResource = useResource<JiraPlanDataRestResponse | null>(
		uifBacklogResourceOld,
	);

	useOnce(() => {
		setMark(USE_RESOURCE_READY_MARK);
	});

	useOnce(
		() => {
			setMark(BACKLOG_PREFETCHED_DATA_READY_MARK);
		},
		() => !!jiraPlanDataRestResource.data,
	);

	const response = useRelayResource<viewBacklogCustomFiltersQuery>(customFiltersResource);
	const customFiltersQueryRef = response.queryReference;

	const isCacheHitRef = useRef(jiraPlanDataRestResource.data != null);
	const isCacheHit = isCacheHitRef.current;

	const { data, promise, loading, error } = jiraPlanDataRestResource;

	const { data: features } = useFeatures();

	const [versionFilterRelayQueryRef, loadVersionFilterRelayQuery] =
		useQueryLoader<viewBacklogVersionFilterRelayQuery>(VERSION_FILTER_RELAY_QUERY);

	useEffect(() => {
		if (loadVersionFilterRelayQuery !== null && data?.projects?.length) {
			const jiraProjectIds = data.projects.map((project) =>
				createAri({
					resourceOwner: 'jira',
					cloudId: tenantContext.cloudId,
					resourceType: 'project',
					resourceId: `${project.id}`,
				}),
			);

			loadVersionFilterRelayQuery({
				jiraProjectIds,
				first: 500,
				after: null,
			});
		}
	}, [loadVersionFilterRelayQuery, tenantContext.cloudId, data?.projects]);

	/**
	 * Redirect to the board only if the backlog is disabled. Do not redirect on timeout.
	 *
	 * If features.hasBacklog is undefined but the resource has returned a 404 due to
	 * the backlog being disabled, this error will be passed through to responseToSla in
	 * the BacklogApp which will redirect to the projects page as a fallback.
	 *
	 * */
	if (loading === false && features?.hasBacklog === false) {
		const boardUrl = getCMPBoardUrl(projectKey, String(boardId));

		return <Redirect to={boardUrl} />;
	}

	const backlogEl = (
		<>
			<SubProductUpdater subProduct="software" />
			<BacklogApp
				rapidViewId={boardId}
				projectKeyFromContext={projectKey}
				projectIdFromContext=""
				tenantContext={tenantContext}
				data={null}
				aggData={null}
				planModeData={data}
				planModeDataPromise={promise}
				error={error}
				loading={loading}
				isCacheHit={isCacheHit}
				customFiltersQueryRef={customFiltersQueryRef}
				versionFilterRelayQueryRef={versionFilterRelayQueryRef}
			/>
			<ResourceRefreshOnMount resource={jiraPlanDataRestResource} refreshOnExpired />
		</>
	);

	return (
		<>
			<Breadcrumbs />
			{backlogEl}
			<RedirectIfProjectArchived projectKey={projectKey} />
		</>
	);
};

export const BacklogMetaPlanDataAPIConsumer = () => {
	const [paramProjectKey] = usePathParam('projectKey');
	const [boardIdStr] = usePathParam('boardId');
	const boardId = Number(boardIdStr);
	const projectKey = paramProjectKey ?? '';

	const tenantContext = useTenantContext();

	useOnce(() => {
		setMark(USE_RESOURCE_START_MARK);
	});

	const uifBacklogDataResult = useResource<UIFBacklogCachedDataResult | null>(uifBacklogResource);

	useOnce(() => {
		setMark(USE_RESOURCE_READY_MARK);
	});

	useOnce(
		() => {
			setMark(BACKLOG_PREFETCHED_DATA_READY_MARK);
		},
		() => !!uifBacklogDataResult.data,
	);

	const response = useRelayResource<viewBacklogCustomFiltersQuery>(customFiltersResource);
	const customFiltersQueryRef = response.queryReference;

	const isCacheHitRef = useRef(Boolean(uifBacklogDataResult.data?.isCacheHit));
	const isCacheHit = isCacheHitRef.current;

	const { data, promise, loading, error } = uifBacklogDataResult;

	const { data: features } = useFeatures();

	const [versionFilterRelayQueryRef, loadVersionFilterRelayQuery] =
		useQueryLoader<viewBacklogVersionFilterRelayQuery>(VERSION_FILTER_RELAY_QUERY);

	useEffect(() => {
		if (loadVersionFilterRelayQuery !== null && data?.result?.projects?.length) {
			const jiraProjectIds = data.result.projects.map((project) =>
				createAri({
					resourceOwner: 'jira',
					cloudId: tenantContext.cloudId,
					resourceType: 'project',
					resourceId: `${project.id}`,
				}),
			);

			loadVersionFilterRelayQuery({
				jiraProjectIds,
				first: 500,
				after: null,
			});
		}
	}, [loadVersionFilterRelayQuery, tenantContext.cloudId, data?.result?.projects]);

	/**
	 * Redirect to the board only if the backlog is disabled. Do not redirect on timeout.
	 *
	 * If features.hasBacklog is undefined but the resource has returned a 404 due to
	 * the backlog being disabled, this error will be passed through to responseToSla in
	 * the BacklogApp which will redirect to the projects page as a fallback.
	 *
	 * */
	if (loading === false && features?.hasBacklog === false) {
		const boardUrl = getCMPBoardUrl(projectKey, String(boardId));

		return <Redirect to={boardUrl} />;
	}

	const backlogEl = (
		<>
			<SubProductUpdater subProduct="software" />
			<BacklogApp
				rapidViewId={boardId}
				projectKeyFromContext={projectKey}
				projectIdFromContext=""
				tenantContext={tenantContext}
				data={null}
				aggData={null}
				cmpBacklogData={data}
				cmpBacklogDataPromise={promise}
				error={error}
				loading={loading}
				isCacheHit={isCacheHit}
				customFiltersQueryRef={customFiltersQueryRef}
				versionFilterRelayQueryRef={versionFilterRelayQueryRef}
			/>
			<ResourceRefreshOnMount resource={uifBacklogDataResult} refreshOnExpired />
		</>
	);

	return (
		<>
			<Breadcrumbs />
			{backlogEl}
			<RedirectIfProjectArchived projectKey={projectKey} />
		</>
	);
};

export default componentWithFG(
	'indexeddb_cache_for_backlog',
	BacklogMetaPlanDataAPIConsumer,
	BacklogMetaPlanDataAPIConsumerOld,
);
